import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import { articles, categories } from "@/mocks/news";

const BASE_URL = "https://jugxooi.cn";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((item) => item.slug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="mx-auto flex min-h-[60vh] max-w-content flex-col items-center justify-center px-4 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">没有找到这个频道</h1>
          <p className="mt-3 text-sm text-foreground-600">你访问的频道可能已经调整或不存在。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const list = articles.filter((article) => article.categorySlug === category.slug);
  const ranking = [...list].sort((a, b) => b.heat - a.heat);
  const others = categories.filter((item) => item.slug !== category.slug);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${category.name}频道 - 美日吃瓜`,
      url: `${BASE_URL}/category/${category.slug}`,
      inLanguage: "zh-CN",
      description: category.description,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${BASE_URL}/` },
        {
          "@type": "ListItem",
          position: 2,
          name: category.name,
          item: `${BASE_URL}/category/${category.slug}`,
        },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${category.name}频道 - 美日吃瓜 | 每日娱乐热点观察`}
        description={`${category.description} 美日吃瓜${category.name}频道每日更新，带你用时间线的方式看清娱乐圈最新动态。`}
        keywords={`美日吃瓜${category.name},美日吃瓜,娱乐圈${category.name},${category.name}最新`}
        canonical={`/category/${category.slug}`}
        ogType="website"
        jsonLd={jsonLd}
      />
      <Navbar />
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: category.name }]} />
      <main>
        <section className="border-b border-background-200 bg-background-50">
          <div className="mx-auto grid max-w-content grid-cols-1 items-center gap-8 px-4 py-12 md:px-6 md:py-14 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.3em] text-primary-600 uppercase">
                <span className="h-px w-6 bg-primary-400" aria-hidden="true" />
                Channel
              </span>
              <h1 className="mt-3 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
                {category.name}
              </h1>
              <p className="mt-2 text-sm font-medium text-accent-700">{category.short}</p>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                {category.description}
              </p>
              <p className="mt-4 text-xs text-foreground-500">
                当前共收录 {list.length} 篇内容 · 每日更新
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="h-52 w-full overflow-hidden rounded-lg border border-background-200 bg-background-200 md:h-60">
                <img
                  src={category.cover}
                  alt={`${category.name}频道主题配图`}
                  title={`${category.name} - 美日吃瓜`}
                  className="h-full w-full object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950">频道内容</h2>
              <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                {list.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>

            <aside className="lg:col-span-4" aria-label="频道热榜">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="flex items-center gap-2 border-b border-background-300 pb-4 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-fire-fill text-primary-500" aria-hidden="true" />
                  频道热榜
                </h2>
                <ol className="mt-2 divide-y divide-background-300">
                  {ranking.map((article, index) => (
                    <li key={article.id}>
                      <Link
                        to={`/article/${article.id}`}
                        title={`${article.title} - 美日吃瓜`}
                        className="group flex items-start gap-3 py-3"
                      >
                        <span
                          className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                            index < 3
                              ? "bg-primary-500 text-background-50"
                              : "bg-background-300 text-foreground-700"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="block text-sm font-medium leading-relaxed text-foreground-800 transition-colors group-hover:text-primary-700">
                          {article.title}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">其他频道</h2>
                <ul className="mt-4 space-y-2.5">
                  {others.map((item) => (
                    <li key={item.slug}>
                      <Link
                        to={`/category/${item.slug}`}
                        className="group flex items-center justify-between rounded-md border border-background-200 px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                      >
                        {item.name}
                        <i
                          className="ri-arrow-right-s-line text-foreground-400 transition-transform group-hover:translate-x-0.5"
                          aria-hidden="true"
                        />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}